import { CapsuleGoods } from "@spwn/types/firebase/firestore";
import type {
  ReqPurchaseCapsule,
  ResPurchaseCapsule,
  ReqPurchaseCapsule3DSComplete,
  ErrorResponsePurchaseCapsule,
} from "@spwn/types/functions";
import appConfig from "constants/appConfig";
import { purchaseActions } from "modules/purchase";
import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { Store } from "store";
import { fetchFbToken } from "utility/firebase";

type ErrorPurchaseCapsule = ErrorResponsePurchaseCapsule["errors"];

export const usePurchaseCapsule = () => {
  const fingerprint = useSelector((state: Store) => state.purchase.fingerprint);
  const [loading, setLoading] = useState(false);
  // @ts-expect-error TS2345
  const [error, setError] = useState<ErrorPurchaseCapsule>(null);
  const [resultLotteryProducts, setResultLotteryProducts] = useState<
    CapsuleGoods[]
  >([]);
  const [resultBonusProducts, setResultBonusProducts] = useState<
    CapsuleGoods[]
  >([]);
  const history = useHistory();
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(purchaseActions.refreshFingerprint());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setPurchaseResults = useCallback(
    (pickedProducts: ResPurchaseCapsule["pickedProducts"]) => {
      const lotteryProducts = pickedProducts
        .filter((p) => p.capsuleGoodsType === "lottery")
        .flatMap((el) => [...Array(el.count)].map((_) => el.product));
      const bonusProducts = pickedProducts
        .filter((p) => p.capsuleGoodsType === "bonus")
        .flatMap((el) => [...Array(el.count)].map((_) => el.product));

      // 通常排出グッズだけ順番をランダムに入れ替える(理由: バックエンドからのデータがソートされてしまっていて、そのまま表示すると偏って排出されたようにも見えてしまうため)
      setResultLotteryProducts(shuffle(lotteryProducts));
      setResultBonusProducts(bonusProducts);
    },
    []
  );

  const purchaseCapsule = useCallback(
    async (
      reqBody: Omit<ReqPurchaseCapsule, "fingerprint">,
      use3DSecure: boolean
    ) => {
      try {
        setLoading(true);
        const responseJson = await callPurchaseCapsule(
          {
            ...reqBody,
            fingerprint,
          },
          use3DSecure
        );
        // error process
        if (responseJson.type === "error") {
          const { errors } = responseJson.body;
          if (errors?.type && errors?.msg) {
            setError({
              type: errors.type,
              msg: errors.msg,
            });
            return;
          }
          setError({
            type: "UNKNOWN_ERROR",
            msg: "予期せぬエラーが発生しました。",
          });
          return;
        }

        if (responseJson.type === "challenge") {
          // 3Dセキュアのチャレンジ認証でリダイレクト
          window.location.href = responseJson.redirectUrl;
          return true;
        }

        // success process. set result products.
        setPurchaseResults(responseJson.body.pickedProducts);
      } catch (error) {
        console.error(error);
        setError({
          type: "UNKNOWN_ERROR",
          msg: "予期せぬエラーが発生しました。",
        });
        return error as Error;
      } finally {
        setLoading(false);
        dispatch(purchaseActions.refreshFingerprint());
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fingerprint]
  );

  // 3Dセキュア認証のチャレンジ認証から帰ってきた後に呼び出す用
  const purchaseCapsule3DSComplete = useCallback(
    async (reqBody: ReqPurchaseCapsule3DSComplete) => {
      try {
        setLoading(true);
        const responseJson = await callPurchaseCapsule3DSComplete(reqBody);
        // error process
        if (responseJson.type === "error") {
          const { errors } = responseJson.body;
          if (errors?.type && errors?.msg) {
            setError({
              type: errors.type,
              msg: errors.msg,
            });
            return;
          }
          setError({
            type: "UNKNOWN_ERROR",
            msg: "予期せぬエラーが発生しました。",
          });
          return;
        }

        // success process. set result products.
        setPurchaseResults(responseJson.body.pickedProducts);

        // URLからチャレンジ認証関連のquery param消す
        const newSearch = new URLSearchParams(history.location.search);
        newSearch.set("3ds", "");
        newSearch.delete("orderId");
        newSearch.delete("AccessID");
        newSearch.delete("AccessPass");
        history.replace({
          ...history.location,
          search: newSearch.toString(),
        });
      } catch (error) {
        console.error(error);
        setError({
          type: "UNKNOWN_ERROR",
          msg: "予期せぬエラーが発生しました。",
        });
      } finally {
        setLoading(false);
        dispatch(purchaseActions.refreshFingerprint());
      }
    },
    [dispatch, history, setPurchaseResults]
  );

  const clearResult = useCallback(() => {
    setResultLotteryProducts([]);
    setResultBonusProducts([]);
  }, []);

  return {
    loading,
    error,
    resultLotteryProducts,
    resultBonusProducts,
    purchaseCapsule,
    purchaseCapsule3DSComplete,
    clearResult,
  };
};

/**
 * Call purchaseCapsule API
 * For inference, return type have same fields in all cases. 推論のため、返り値に同じフィールドを持たせている。
 * @param reqBody
 * @returns
 */
const callPurchaseCapsule = async (
  reqBody: ReqPurchaseCapsule,
  use3DSecure: boolean
): Promise<
  | {
      type: "success";
      body: ResPurchaseCapsule;
    }
  | {
      type: "challenge";
      redirectUrl: string;
    }
  | {
      type: "error";
      body: ErrorResponsePurchaseCapsule;
    }
> => {
  // return {
  //   type: "success",
  //   body: {
  //     pickedProducts: getMockResults(),
  //   }
  // }
  // buy process
  // isCrowding入れた方がいい？
  const fbToken = await fetchFbToken();
  const endpoint = use3DSecure
    ? appConfig.CloudFunctions.purchaseCapsule3DS
    : appConfig.CloudFunctions.purchaseCapsule; // "http://localhost:5000/bls-boost/us-central1/purchaseCapsule"
  // @ts-expect-error TS2769
  const response = await fetch(endpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `'Bearer ${fbToken}`,
    },
    body: JSON.stringify(reqBody),
  });
  if (response.status === 200) {
    const responseBody = await response.json();
    if (use3DSecure && responseBody.acs === "2") {
      return {
        type: "challenge",
        redirectUrl: responseBody.redirectUrl,
      };
    }
    return {
      type: "success",
      body: responseBody,
    };
  }
  const errorResponse: ErrorResponsePurchaseCapsule = await response.json();
  console.info(errorResponse);
  return {
    type: "error",
    body: errorResponse,
  };
};

const callPurchaseCapsule3DSComplete = async (
  reqBody: ReqPurchaseCapsule3DSComplete
): Promise<
  | {
      type: "success";
      body: ResPurchaseCapsule;
    }
  | {
      type: "error";
      body: ErrorResponsePurchaseCapsule;
    }
> => {
  const fbToken = await fetchFbToken();
  const response = await fetch(
    // @ts-expect-error TS2769
    appConfig.CloudFunctions.purchaseCapsule3DSComplete,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `'Bearer ${fbToken}`,
      },
      body: JSON.stringify(reqBody),
    }
  );
  if (response.status === 200) {
    const responseBody = await response.json();
    return {
      type: "success",
      body: responseBody,
    };
  }
  const errorResponse: ErrorResponsePurchaseCapsule = await response.json();
  console.info(errorResponse);
  return {
    type: "error",
    body: errorResponse,
  };
};

/**
 * @description Fisher-Yatesアルゴリズムを用いて、配列をランダムに並び替えて返す関数
 * @param _array ランダムに並び替えたい配列()
 * @returns ランダムに並び替えた配列
 */
const shuffle = <T>(array: T[]): T[] => {
  const _array = [...array];
  for (let i = _array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    // swap
    [_array[i], _array[j]] = [_array[j], _array[i]] as [T, T];
  }
  return _array;
};
